import React, { useState } from "react";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";

/**
 * The warehouse switch: keep part of the stock somewhere other than the shop.
 *
 * Off, nothing in the dashboard mentions a warehouse. On, products carry a
 * second count, deliveries can be sent there, stock can be moved between the
 * two — and the register still sells only what is on the shelf. It saves the
 * moment it is flipped, and the shop everywhere else is refreshed with it.
 * The server refuses to switch it off while stock is still in it.
 */
const WarehouseOption = ({ shop, onSaved }) => {
  const { t } = useI18n();
  const { can, setShop: setMembershipShop } = useMembership();
  const canEdit = can("shop.edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const on = Boolean(shop.warehouse_enabled);

  const flip = async () => {
    setSaving(true);
    setError("");

    try {
      const { data } = await shopsAPI.update({ warehouse_enabled: !on });
      onSaved(data.shop);
      // The sidebar, the stock screen and the deliveries read it from here
      setMembershipShop({ ...(data.shop || {}) });
    } catch (err) {
      setError(err.response?.data?.message || t("shop.warehouse.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t("shop.warehouse.title")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("shop.warehouse.hint")}</p>

      {error && (
        <p role="alert" className="mb-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p id="warehouse-label" className="font-medium text-gray-900">
            {t("shop.warehouse.label")}
          </p>
          <p className="text-sm text-gray-500">{t(`shop.warehouse.${on ? "on" : "off"}`)}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-labelledby="warehouse-label"
          disabled={!canEdit || saving}
          onClick={flip}
          className={`relative shrink-0 w-12 h-7 rounded-full transition-colors disabled:opacity-60 ${
            on ? "bg-primary-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${
              on ? "start-6" : "start-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default WarehouseOption;
